import { useQuery, useQueryClient } from "@tanstack/react-query";
import { boronClient } from "../../api";
import { createError } from "../../errors";
import { KarteTemplateInterface } from "../../interfaces/KarteTemplateInterface";

const path = "/api/v1/sections/{section_id}/karte_templates" as const;

const buildQueryKey = (sectionId: string) => [path, sectionId];

export const useFetchKarteTemplates = ({
  sectionId,
}: {
  sectionId: string;
}) => {
  return useQuery<KarteTemplateInterface[]>({
    queryKey: buildQueryKey(sectionId),
    queryFn: async () => {
      const { response, data } = await boronClient.GET(path, {
        params: { path: { section_id: sectionId } },
      });
      if (response.ok && data) {
        return data.karteTemplates;
      }
      throw await createError(response);
    },
  });
};

export const useKarteTemplatesCacheUpdater = () => {
  const queryClient = useQueryClient();

  const invalidateKarteTemplates = (sectionId: string) => {
    queryClient.resetQueries({ queryKey: buildQueryKey(sectionId) });
  };

  const updateKarteTemplates = (
    sectionId: string,
    newKarteTemplate: KarteTemplateInterface,
  ) => {
    queryClient.setQueryData<KarteTemplateInterface[]>(
      buildQueryKey(sectionId),
      (oldData) => {
        if (!oldData) {
          return [];
        }
        return oldData.map((karteTemplate) =>
          karteTemplate.id === newKarteTemplate.id
            ? newKarteTemplate
            : karteTemplate,
        );
      },
    );
  };

  const removeKarteTemplate = (sectionId: string, karteTemplateId: string) => {
    queryClient.setQueryData<KarteTemplateInterface[]>(
      buildQueryKey(sectionId),
      (oldData) => {
        if (!oldData) {
          return [];
        }
        return oldData.filter(
          (karteTemplate) => karteTemplate.id !== karteTemplateId,
        );
      },
    );
  };

  return {
    invalidateKarteTemplates,
    updateKarteTemplates,
    removeKarteTemplate,
  };
};
